import { ParamDefRowView, ParamValue, paramValueSchema } from "@bonusmap/api-types";
import { StepsComponent } from "../base-components/steps-component.js";
import { BonusParamItemComponent } from "./bonus-param-item.js";
import { templateParseToElement } from "../../logic/templateParse.js";
import { ValueArrayInput } from "../value-array-input/value-array-input.js";
import { toastError, toastSuccess } from "../../toasts.js";
import { isNumber } from "@bonusmap/shared_ui/library";
type BonusParamItemValueSteps = Record<"view" | "edit", string>;
export class BonusParamItemValue extends StepsComponent {
    static webComponentName = "bonus-param-item-value";
    protected _templateId: string = "bonus-param-item-value";
    private _value!: ParamValue;
    get value(): ParamValue {
        return this._value;
    }
    set value(value: ParamValue) {
        if (this.isValidValue(value)) {
            this._value = value;
            this.dispatch('value-changed', { value , origin: this } , true, true);
            this.render();
        } else {
            console.error("Invalid value for BonusParamItemValue:", value);
        }
    }
    resetValue(): void {
        this.value = ""; // Reset to an empty value or a default value
    }
    private _currentParam!: ParamDefRowView;
    get currentParam(): ParamDefRowView {
        return this._currentParam;
    }
    set currentParam(value: ParamDefRowView) {
        this._currentParam = value;
        // this.dispatch('param-changed', { value });
    }

    private _rendered: boolean = false;
    public get rendered(): boolean {
        return this._rendered;
    }
    private set rendered(value: boolean) {
        this._rendered = value;
    }

    private hostComponent!: BonusParamItemComponent;

    protected _step: string = "bonus-param-item-value-view";
    public readonly steps: BonusParamItemValueSteps = {
        "view": "bonus-param-item-value-view",
        "edit": "bonus-param-item-value-edit"
    };
    constructor() {
        super();
        //inizialmente prende il valore dal contenuto dell'elemento
        this.initializeValue();
        this.globalEventListeners();
        this.initializeHostComponent();
    }

    private globalEventListeners(): void {
        this.on('click', `[role="value-save"]`, (event: Event) => {
            event.preventDefault();
            this.saveValue();
        })
    }
    private initializeHostComponent(): void {
        this.hostComponent = this.closest('bonus-param-item') as BonusParamItemComponent;
        this.hostComponent?.addEventListener('param-changed', (event: Event) => {
            const ev: CustomEvent = event as CustomEvent;
            const current = ev.detail.value;
            if (current) {
                this.currentParam = current;
            }
            this.renderInput();
        });

    }
    public putInEditMode(dispatchEvent = false): void {
        this.goToStep(this.steps.edit, this, dispatchEvent);
    }
    public putInViewMode(dispatchEvent = false): void {
        this.goToStep(this.steps.view, this, dispatchEvent);
    }
    private initializeValue(): void {
        const initialValueString = this.textContent?.trim();
        if (initialValueString) {
            try {
                const parsedValue = JSON.parse(initialValueString);
                if (this.isValidValue(parsedValue)) {
                    this._value = parsedValue;
                } else {
                    console.error("Invalid initial value for BonusParamItemValue:", parsedValue);
                }
            } catch (error) {
                // console.log("BonusParamItemValue: Error parsing initial value:", error);
                // console.log("Initial value string:", initialValueString);
            }

        } else {
            this._value = ""; // or some default value
        }
        this.textContent = ""; // Clear the text content after initialization
        this.dispatch('loaded', { origin: this }, true, true); // Dispatch loaded event
    }

    private isValidValue(value: any): value is ParamValue {
        const isValid = paramValueSchema.safeParse(value);

        if (!isValid.success) {
            console.error("BonusParamItemValue: Invalid value:", value, isValid.error);
            return false;
        }
        // If the value is valid according to the schema, return true
        return true;
    }
    get computedValue(): string {
        if (this.value === undefined || this.value === null || this.value === "") {
            return "valore non definito";
        }
        if (typeof this.value === "string" || typeof this.value === "number") {
            return String(this.value);
        }
        if (typeof this.value === "boolean") {
            return (this.value) ? "Sì" : "No";
        }
        if (typeof this.value === "object" && this.value !== undefined) {
            if (Array.isArray(this.value)) {
                if (this.value.length === 2 && this.currentParam && this.currentParam.type_value && this.currentParam.type_value.startsWith('range')) {
                    return `da ${this.value[0]} a ${this.value[1]}`;
                }
                return this.value.join(", ");
            }
            return JSON.stringify(this.value);
        }
        return "valore non definito";
    }
    private saveValue(): void {
        if (this.currentParam && this.bonusParamItemValueEdit) {
            const typeValue = this.currentParam.type_value;
            let value: any = null;
            let errorMessage: string = "";
            if (typeValue == "boolean") {
                value =
                    this.bonusParamItemValueEdit.querySelector<HTMLInputElement>('input[type="checkbox"]')?.checked;
            }
            else if (typeValue == "number" || typeValue == "date") {
                let v: any =
                    this.bonusParamItemValueEdit.querySelector<HTMLInputElement>('input[type]')?.value;
                v = v ? (typeValue == "date" ? new Date(v) : parseFloat(v)) : null;
                // Check if the value is a valid number or date
                if (typeValue == "number" && !isNumber(v)) {
                    errorMessage = "Il valore deve essere un numero valido.";
                }
                else if (typeValue == "date" && !(v instanceof Date)) {
                    errorMessage = "Il valore deve essere una data valida.";
                }
                else {
                    value = v;
                }
            } else if (typeValue == "range_date" || typeValue == "range_number") {
                const valueMinInput = this.bonusParamItemValueEdit.querySelector<HTMLInputElement>('[name="value_min"]');
                const valueMaxInput = this.bonusParamItemValueEdit.querySelector<HTMLInputElement>('[name="value_max"]');
                let valueMin = valueMinInput ? valueMinInput.value : null;
                let valueMax = valueMaxInput ? valueMaxInput.value : null;
                const inputs = [valueMin, valueMax].filter(v => v !== null && v !== undefined);
                const results: (number | Date)[] = [];
                inputs.forEach((v: any, index) => {
                    if (typeValue == "range_date") {
                        v = new Date(v);
                        if (!isNumber(v.getTime())) {
                            errorMessage = `Il valore ${index === 0 ? 'minimo' : 'massimo'} deve essere una data valida.`;
                        }
                        else {
                            results.push(v);
                        }
                    } else if (typeValue == "range_number") {
                        v = parseFloat(v);
                        if (!isNumber(v)) {
                            errorMessage = `Il valore ${index === 0 ? 'minimo' : 'massimo'} deve essere un numero valido.`;
                        }
                        else {
                            results.push(v);
                        }
                    }
                });
                if (!errorMessage) {
                    value = results;
                }
            }
            else if (typeValue == "date_array" || typeValue == "number_array") {
                const valueArrayInput = this.bonusParamItemValueEdit.querySelector<ValueArrayInput>('value-array-input');
                if (valueArrayInput) {
                    // nel caso di date_array i valori devono essere convertiti in Date
                    const values = valueArrayInput.values;
                    if (typeValue == "date_array") {
                        value = values.map(v => new Date(v));
                        if (value.some((v: Date) => !isNumber(v.getTime()))) {
                            errorMessage = "Tutti i valori devono essere date valide.";
                        }
                    } else if (typeValue == "number_array") {
                        value = values.map(v => typeof v !== "number" ? parseFloat(v) : v);
                    }
                } else {
                    errorMessage = "Il campo di input per l'array non è stato trovato.";
                }
            }
            if (errorMessage) {
                toastError(errorMessage, "Errore");
                return;
            }
            if (this.isValidValue(value)) {
                this.dispatch('value-changed', { value });
                this.value = value;
                this.goToStep(this.steps.view, this.bonusParamItemValueEdit);
                toastSuccess("Valore salvato", "Informazione");
            }
            else {
                toastError("Il valore inserito non è valido.", "Errore");
            }
        }
        else {
            return;
        }
    }
    private get hideCloseButton(): boolean {
        return this.attributeBoolOption('hide-close-button', false);
    }
    protected buildRenderingData(): Record<string, any> {
        const data = super.buildRenderingData();

        return {
            ...data,
            hideCloseButton: this.hideCloseButton,
            value: this.value,
            computedValue: this.computedValue,
            steps: this.steps,
        };
    }

    private bonusParamItemValueEdit!: HTMLElement;
    private renderInput(): void {
        const bonusParamItemValueEdit = this.roleElementSelector(this.steps.edit);
        this.bonusParamItemValueEdit = bonusParamItemValueEdit as HTMLElement;
        if (!bonusParamItemValueEdit) {
            console.warn("BonusParamItemValue: bonus-param-item-value-edit element not found.");
            return;
        }
        const currentParam = this.currentParam;
        if (currentParam) {
            currentParam.type_value
        }
        templateParseToElement('bonus-param-item-value-edit', (bonusParamItemValueEdit as HTMLElement), {
            type_value: currentParam?.type_value,
            value: this.value,
            controlsId: this.createRandomId(),
            hideCloseButton: this.hideCloseButton,
            steps: this.steps,
        })
    }
    protected afterRender(): void {
        super.afterRender();
        this.renderInput();
        this.rendered = true;
    }
    protected render(): void {
        super.render();
    }
}